import { useTranslation } from "react-i18next";
import { useCallback } from "react";

/**
 * Dev-only: a server-driven label with no `nav` entry silently renders the
 * API's English inside an Arabic UI — no lint rule can see it, because the
 * string never appears in source. Say so once per missing slug, naming the
 * key to add.
 */
const warnedMissing = new Set<string>();
const warnMissingNavKey = (kind: string, slugs: string[], fallback: string) => {
  // An empty slug means the label was not English to begin with (a caller
  // passing an already-translated name) — nothing to look up.
  if (
    !import.meta.env.DEV ||
    !slugs[0] ||
    warnedMissing.has(`${kind}:${slugs[0]}`)
  )
    return;
  warnedMissing.add(`${kind}:${slugs[0]}`);
  console.warn(
    `[i18n] No nav:${kind}.* entry for "${fallback}" — rendering it untranslated. ` +
      `Add one of: ${slugs.map((s) => `nav:${kind}.${s}`).join(", ")} ` +
      `to public/locales/{en,ar}/nav.json.`,
  );
};

/**
 * Translates server-driven navigation labels.
 *
 * Sidebar modules and their categories arrive from the permissions API already
 * named in English, so they cannot be translated at the call site the usual
 * way. Instead we look them up in the `nav` namespace by their stable slug
 * (`item.key`, e.g. `time-attendance`) and fall back to the API's own string
 * when no translation exists — so a module added on the backend keeps working,
 * untranslated, instead of rendering a raw key.
 */
export const useNavTranslation = () => {
  const { t, i18n } = useTranslation("nav");
  // Before the namespace arrives every lookup misses; only warn once it has.
  const navLoaded = i18n.hasLoadedNamespace("nav");

  /**
   * Looks a module up by its stable slug, then — if the backend's key differs
   * from what we ship — by a slug of its English display name. Two chances mean
   * a key rename on the server does not silently drop back to English.
   *
   * @param key stable module slug — `ISidebarLink.key`.
   * @param fallback the API's own (English) label.
   */
  const translateModule = useCallback(
    (key: string, fallback: string) => {
      const byKey = t(`modules.${key}`, { defaultValue: "" });
      if (byKey) return byKey;
      const byName = t(`modules.${slugify(fallback)}`, { defaultValue: "" });
      if (byName) return byName;
      // Also try the hyphenated form, which is how most module keys look.
      const byNameDashed = t(
        `modules.${slugify(fallback).replace(/_/g, "-")}`,
        {
          defaultValue: "",
        },
      );
      if (byNameDashed) return byNameDashed;
      if (navLoaded) {
        warnMissingNavKey(
          "modules",
          [key, slugify(fallback).replace(/_/g, "-")].filter(Boolean),
          fallback,
        );
      }
      return fallback;
    },
    [t, navLoaded],
  );

  const translateCategory = useCallback(
    (name: string, fallback?: string) => {
      const translated = t(`categories.${slugify(name)}`, { defaultValue: "" });
      if (translated) return translated;
      if (navLoaded) warnMissingNavKey("categories", [slugify(name)], name);
      return fallback ?? name;
    },
    [t, navLoaded],
  );

  /** Page-level tab labels (from `nav-tabs.config`), keyed by their English text. */
  const translateTab = useCallback(
    (label: string) => t(`tabs.${slugify(label)}`, { defaultValue: label }),
    [t],
  );

  return { translateModule, translateCategory, translateTab };
};

/** "Time & Attendance" → "time_attendance" — a stable, JSON-safe key. */
const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

export { slugify as slugifyNavKey };
